import React from 'react';
import { Container as ModalContainer } from 'react-modal-promise';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions
} from '@auth0/auth0-react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';

import { queryClient } from '@/adapters/query';

import './lib/loadGoogleMaps';
import './lib/loadUpscope';

import { ErrorBoundaryFallback } from './components/ErrorBoundaryFallback';
import Layout from './components/Layout';
import { Pylon } from './lib/Pylon';
import UnauthorizedPage from './pages/UnauthorizedPage';
import { RoutePath } from './routes/RoutePath';

import { RouteConfig } from './routes';
import theme from './theme';

import './styles.css';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_PRO_LICENSE_KEY);
new Pylon().load();

interface Auth0ProviderWithRedirectCallbackProps extends Auth0ProviderOptions {
  children: React.ReactNode;
}

const Auth0ProviderWithRedirectCallback: React.FC<
  Auth0ProviderWithRedirectCallbackProps
> = ({ children, ...props }) => {
  const navigate = useNavigate();
  const onRedirectCallback: (appState: AppState | undefined) => void = (
    appState
  ) => {
    navigate((appState && appState.returnTo) || window.location.pathname, {
      state: {
        isFirstLogin: true
      }
    });
  };
  return (
    <Auth0Provider onRedirectCallback={onRedirectCallback} {...props}>
      {children}
    </Auth0Provider>
  );
};

const App = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Sentry.ErrorBoundary
          showDialog
          fallback={({ error, resetError }) => (
            <ErrorBoundaryFallback error={error} resetError={resetError} />
          )}
        >
          <BrowserRouter>
            <Auth0ProviderWithRedirectCallback
              domain={import.meta.env.VITE_AUTH0_DOMAIN}
              clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
              redirectUri={location.origin}
              audience={import.meta.env.VITE_AUTH0_AUDIENCE}
            >
              <QueryClientProvider client={queryClient}>
                <SnackbarProvider maxSnack={3} autoHideDuration={6000}>
                  <Routes>
                    <Route
                      path={RoutePath.Unauthorized}
                      element={<UnauthorizedPage />}
                    />
                    <Route
                      path="*"
                      element={
                        <Layout>
                          <RouteConfig />
                        </Layout>
                      }
                    />
                  </Routes>
                  <ModalContainer />
                </SnackbarProvider>
                <ReactQueryDevtools
                  toggleButtonProps={{
                    style: {
                      left: 16,
                      bottom: 10
                    }
                  }}
                />
              </QueryClientProvider>
            </Auth0ProviderWithRedirectCallback>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
